"use client";

import "@/lib/i18n";

import { SyntheticEvent, Suspense, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocaleProvider } from "@/components/custom/contexts/localeContext";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth/AuthContext";
import { getAccessToken, getAssistantBaseUrl } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";

const baseUrl = getAssistantBaseUrl();

async function postLogin(
  authHeader: string,
  asAdmin = false,
): Promise<boolean> {
  const url = `${baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`}api/auth/login`;
  const headers: Record<string, string> = { Authorization: authHeader };
  if (asAdmin) headers["X-Admin-Mode"] = "true";

  const res = await fetch(url, {
    method: "POST",
    headers,
    credentials: "same-origin",
  });
  return res.ok;
}

function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [adminMode, setAdminMode] = useState(false);
  const showAdminModeToggle = import.meta.env.PROD === false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const ok = await postLogin(`Bearer ${token}`);
        if (!ok) throw new Error("Bearer login rejected by backend");
        await auth.refresh();
        if (!cancelled) {
          navigate(searchParams.get("returnUrl") ?? "/", { replace: true });
        }
      } catch (e) {
        ClientLogger.info("Bearer auto-login unavailable — showing form", {
          error: e instanceof Error ? e : undefined,
          component: "LoginForm",
        });
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams, auth]);

  if (checking) return null;

  async function handleSubmit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const form = e.currentTarget;
    const username = (form.elements.namedItem("username") as HTMLInputElement)
      .value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;
    const credentials = btoa(`${username}:${password}`);

    try {
      const ok = await postLogin(`Basic ${credentials}`, adminMode);
      if (!ok) {
        setError(t("login.invalidCredentials"));
        return;
      }
      await auth.refresh();
      navigate(searchParams.get("returnUrl") ?? "/", { replace: true });
    } catch (err) {
      ClientLogger.error("Login request failed", {
        error: err instanceof Error ? err : undefined,
        component: "LoginForm",
      });
      setError(t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#f5f5f5",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          padding: "2rem",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          width: "100%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <img src="/praetor-avatar.png" alt="Praetor" width={64} height={64} />
        </div>
        <h1
          className="font-serif italic"
          style={{ margin: 0, fontSize: "1.25rem", fontWeight: 500 }}
        >
          {t("login.title")}
        </h1>

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}
        >
          <label
            htmlFor="username"
            style={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            {t("login.username")}
          </label>
          <input
            id="username"
            name="username"
            type="text"
            required
            autoComplete="username"
            style={{
              padding: "0.5rem",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "0.875rem",
            }}
          />
        </div>

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}
        >
          <label
            htmlFor="password"
            style={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            {t("login.password")}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            autoComplete="current-password"
            style={{
              padding: "0.5rem",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "0.875rem",
            }}
          />
        </div>

        {showAdminModeToggle && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#111827",
            }}
          >
            <span>{t("login.adminMode")}</span>
            <Switch
              checked={adminMode}
              onCheckedChange={setAdminMode}
              aria-label={t("login.adminMode")}
            />
          </div>
        )}

        {error && (
          <p style={{ margin: 0, color: "#dc2626", fontSize: "0.875rem" }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            padding: "0.625rem",
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? t("login.submitting") : t("login.submit")}
        </button>
      </form>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LocaleProvider>
        <LoginForm />
      </LocaleProvider>
    </Suspense>
  );
}
